'use client'

import clsx from 'clsx'
import Image from 'next/image'
import { useFormatter, useLocale, useTranslations } from 'next-intl'
import { type ComponentType, useState } from 'react'

import {
  useFanIncome,
  useFanIncomeCountries
} from '@/features/clubs/hooks/useClubMonetization'
import {
  FAN_INCOME_PLATFORMS,
  type FanIncomeItem
} from '@/features/clubs/lib/clubMonetization'
import FilterDropdown from '@/features/leaderboard/components/FilterDropdown'
import {
  CountryFlag,
  FanAvatar
} from '@/features/leaderboard/components/RowParts'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import {
  FacebookIcon,
  InstagramIcon,
  TiktokIcon,
  XIcon,
  YoutubeIcon
} from '@/ui/icons/SocialIcons'
import MediaImage from '@/ui/MediaImage'
import { shouldUnoptimizeImageSrc } from '@/utils/imageUrl'
import { regionNameFromCode } from '@/utils/regionNameFromCode'

import S from './ClubMonetization.module.scss'

const PLATFORM_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  youtube: YoutubeIcon,
  tiktok: TiktokIcon,
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  x: XIcon
}

const PLATFORM_LABELS: Record<string, string> = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  x: 'X'
}

const TASK_TYPES = ['watch', 'share', 'action', 'create'] as const

const TypeBadge = ({ item }: { item: FanIncomeItem }) => {
  const t = useTranslations('club.monetization.fanActivity')
  const type = (TASK_TYPES as readonly string[]).includes(item.task.type)
    ? (item.task.type as (typeof TASK_TYPES)[number])
    : null
  const Icon = item.task.platform
    ? PLATFORM_ICONS[item.task.platform]
    : undefined
  return (
    <span className={clsx(S.typeBadge, type && S[`type_${type}`])}>
      {type ? t(`types.${type}`) : item.task.type}
      {Icon ? (
        <span
          className={S.typeIcon}
          title={PLATFORM_LABELS[item.task.platform!]}
        >
          <Icon />
        </span>
      ) : null}
    </span>
  )
}

const Row = ({ item }: { item: FanIncomeItem }) => {
  const t = useTranslations('club.monetization.fanActivity')
  const format = useFormatter()
  const locale = useLocale()
  const [expanded, setExpanded] = useState(false)
  const iso2 = item.fan.country_iso2 ?? ''
  const country = iso2
    ? regionNameFromCode(locale, iso2)
    : item.fan.country_name ?? ''
  const date = format.dateTime(new Date(item.earned_at), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

  return (
    <li className={clsx(S.activityRow, expanded && S.activityRowExpanded)}>
      <div className={S.fan}>
        <FanAvatar src={item.fan.avatar_url} alt={item.fan.name} compact />
        <div className={S.fanText}>
          <span className={S.rowTitle}>{item.fan.name}</span>
          {country ? (
            <span className={S.rowSub}>
              <CountryFlag code={iso2} />
              {country}
            </span>
          ) : null}
        </div>
      </div>
      <span className={clsx(S.cellDate, S.detail)}>
        <span className={S.detailLabel}>{t('columns.date')}</span>
        {date}
      </span>
      <div className={clsx(S.cellTask, S.detail)}>
        <span className={S.detailLabel}>{t('columns.task')}</span>
        <span className={S.task}>
          {item.task.brand_logo_url ? (
            <MediaImage
              src={item.task.brand_logo_url}
              alt={item.task.brand_name ?? ''}
              width={32}
              height={32}
              className={S.brandLogo}
              unoptimized={shouldUnoptimizeImageSrc(item.task.brand_logo_url)}
            />
          ) : null}
          <span className={S.taskTitle}>{item.task.title}</span>
        </span>
      </div>
      <span className={S.cellType}>
        <TypeBadge item={item} />
      </span>
      <span className={S.cellEarned}>
        {formatLeaderboardNumber(item.earned_ap)}
        <span className={S.mobileOnly}> AP</span>
      </span>
      <button
        type="button"
        className={S.moreButton}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {expanded ? t('less') : t('more')}
      </button>
    </li>
  )
}

/** AP the club's fans brought by completing tasks, newest first (design). */
const FanActivity = () => {
  const t = useTranslations('club.monetization.fanActivity')
  const locale = useLocale()
  const [platform, setPlatform] = useState('')
  const [country, setCountry] = useState('')
  const { data: countries } = useFanIncomeCountries()
  const {
    data,
    isError,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useFanIncome({ platform, country })
  const items = data?.pages.flatMap(page => page?.items ?? []) ?? []

  return (
    <section className={S.activity} aria-labelledby="fan-activity-title">
      <h2 id="fan-activity-title" className={S.sectionTitle}>
        {t('title')}
      </h2>
      <div className={S.filters}>
        <FilterDropdown
          label={t('countryFilter')}
          value={country}
          onChange={setCountry}
          options={[
            { value: '', label: t('allCountries') },
            ...(countries ?? []).map(c => ({
              value: c.iso2,
              label: regionNameFromCode(locale, c.iso2) || c.name
            }))
          ]}
        />
        <FilterDropdown
          label={t('socialFilter')}
          value={platform}
          onChange={setPlatform}
          options={[
            { value: '', label: t('allSocials') },
            ...FAN_INCOME_PLATFORMS.map(p => ({
              value: p,
              label: PLATFORM_LABELS[p]
            }))
          ]}
        />
      </div>

      <div className={S.activityHead} aria-hidden>
        <span>{t('columns.user')}</span>
        <span>{t('columns.date')}</span>
        <span>{t('columns.task')}</span>
        <span className={S.center}>{t('columns.type')}</span>
        <span className={S.center}>{t('columns.earned')}</span>
      </div>

      {isError ? (
        <div className={S.emptyState}>
          <p className={S.emptyTitle}>{t('error')}</p>
          <button type="button" className={S.retry} onClick={() => refetch()}>
            {t('retry')}
          </button>
        </div>
      ) : isLoading ? (
        <div className={S.activitySkeleton} aria-busy="true" />
      ) : items.length === 0 ? (
        <div className={clsx(S.emptyState, S.activityEmpty)}>
          <Image
            src="/assets/club-profile/operations-empty.png"
            alt=""
            width={96}
            height={96}
          />
          <p className={S.emptyTitle}>{t('empty')}</p>
        </div>
      ) : (
        <ul className={S.activityList}>
          {items.map(item => (
            <Row key={item.id} item={item} />
          ))}
        </ul>
      )}

      {hasNextPage ? (
        <button
          type="button"
          className={S.loadMore}
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {t('loadMore')}
        </button>
      ) : null}
    </section>
  )
}

export default FanActivity
